import type { ExpiryTimeGranularity } from '@x-guard/xgac-types/xgac';
import { useCallback } from 'react';
import { required, useTranslate } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { Stack } from '@mui/material';
import { GrayDateInputWithUpperLabel } from './inputs/GrayDateInputWithUpperLabel';
import { ExpiryDateDisplay } from './ExpiryDateDisplay';
import { notLaterThan, notLaterThanToday } from '../../../utils/dateValidators';
import { getAutofillDatesFromFirstIssued } from '../../../utils/certificationDateAutofill';
import { isCertificateDateSource, useCertificateDateOriginsContext } from './CertificateDateOriginContext';

const useFirstIssuedAutofill = (
  firstIssuedOnSource: string,
  lastReceivedOnSource: string,
  expiryDateSource: string,
  validityPeriod: ExpiryTimeGranularity | undefined,
) => {

  const { setValue, trigger } = useFormContext();
  const { setOrigin } = useCertificateDateOriginsContext();

  return useCallback((firstIssuedOn: string) => {

    const autofill = getAutofillDatesFromFirstIssued(firstIssuedOn, validityPeriod);
    if (!autofill) return;

    setValue(lastReceivedOnSource, autofill.lastReceivedOn, { shouldDirty: true, shouldValidate: true });
    setValue(expiryDateSource, autofill.expiryDate, { shouldDirty: true, shouldValidate: true });

    if (isCertificateDateSource(lastReceivedOnSource)) setOrigin(lastReceivedOnSource, 'calculated');
    if (isCertificateDateSource(expiryDateSource)) setOrigin(expiryDateSource, 'calculated');

    trigger([firstIssuedOnSource, lastReceivedOnSource, expiryDateSource]);

  }, [expiryDateSource, firstIssuedOnSource, lastReceivedOnSource, setValue, trigger, validityPeriod, setOrigin]);

};

export const CertificationUserDateFields = (props: {
  validityPeriod: ExpiryTimeGranularity | undefined;
  isProcessing?: boolean;
}) => {

  const translate = useTranslate();

  const autofillFromFirstIssued = useFirstIssuedAutofill(
    'firstIssuedOn',
    'lastReceivedOn',
    'expiryDate',
    props.validityPeriod,
  );

  return (
    <Stack spacing={3}>
      <GrayDateInputWithUpperLabel
        source="firstIssuedOn"
        label="resources.assets.certifications.fields.first_issued_on"
        validate={[
          required(),
          notLaterThanToday('resources.assets.certifications.validation.first_issued_in_future'),
          notLaterThan(
            'lastReceivedOn',
            'resources.assets.certifications.validation.first_issued_later_than_last_received',
          ),
        ]}
        isRequired
        fullWidth
        helperText={false}
        onChange={(event) => {

          if (event.target.value) autofillFromFirstIssued(event.target.value);

        }}
      />
      <ExpiryDateDisplay
        startDateSource="lastReceivedOn"
        expiryDateSource="expiryDate"
        timeGranularity={props.validityPeriod}
        startDateLabel="resources.assets.certifications.fields.last_received_on"
        expiryDateLabel="resources.assets.certifications.fields.expiry_date"
        helperText={translate('resources.assets.certifications.text.expiry_date_input_helper_text')}
        usage="certifications"
        minDateSource="firstIssuedOn"
        isRequired
        isProcessing={props.isProcessing}
      />
    </Stack>
  );

};
